/* eslint-disable no-alert */
import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import { object, string, number } from 'yup';

import useHttpStatus from '~/commons/hooks/useHttpStatus';

import ListHttpTable from '~/commons/components/ListHttpTable.jsx';
import ModalConfirm from '~/commons/components/ModalConfirm.jsx';
import Header from '../../layout/Header.jsx';
import FormHttpAlert from '../../../../commons/components/FormHttpAlert.jsx';
import { getLang } from '../../../../commons/helpers.js';
import { subjects } from '../../../../../../../components/quiz/config/subjects';

const PageAssignments = () => {
  const [selectedPreset, setSelectedPreset] = useState('');
  const [search, setSearch] = useState('');
  const [removeConfirm, setRemoveConfirm] = useState({
    active: false,
    key: null,
  });
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [currentAssignmentId, setCurrentAssignmentId] = useState('');
  const [editData, setEditData] = useState({
    startDate: '',
    endDate: '',
    articles: [],
    geography: '',
    economicDevelop: '',
    society: '',
    technological: '',
    video: '',
  });
  const [res, getReq] = useHttpStatus();
  const { t } = useTranslation();

  const preSetAssignments = [
    { key: 'period1_p1', lv: 'p1', label: t('Period 1 - Junior Primary') },
    { key: 'period1_p2', lv: 'p2', label: t('Period 1 - Senior Primary') },
    { key: 'period2_p1', lv: 'p1', label: t('Period 2 - Junior Primary') },
    { key: 'period2_p2', lv: 'p2', label: t('Period 2 - Senior Primary') },
    { key: 'period3_p1', lv: 'p1', label: t('Period 3 - Junior Primary') },
    { key: 'period3_p2', lv: 'p2', label: t('Period 3 - Senior Primary') },
  ];

  const schema = object({
    startDate: string().required('Start date is required'),
    endDate: string().required('End date is required'),
    geography: number()
      .required('Geography is required')
      .min(1, 'Must be at least 1'),
    economicDevelop: number()
      .required('Economic Development is required')
      .min(1, 'Must be at least 1'),
    society: number()
      .required('Society is required')
      .min(1, 'Must be at least 1'),
    technological: number()
      .required('Technological is required')
      .min(1, 'Must be at least 1'),
    video: number().required('Video is required').min(1, 'Must be at least 1'),
  });

  // Article count validation state
  const [articleCountError, setArticleCountError] = useState('');

  const fetchAssignment = async () => {
    const response = await getReq({
      url: '/api/quiz/assignments',
      params: {
        filter: { key: selectedPreset },
        _t: Date.now(),
      },
    });
    if (
      response
      && response.payload
      && response.payload.rows
      && response.payload.rows.length > 0
    ) {
      const assignment = response.payload.rows[0];
      setEditData({
        startDate: moment(assignment.startDate).format('YYYY-MM-DD'),
        endDate: moment(assignment.endDate).format('YYYY-MM-DD'),
        articles: assignment.articles || [],
        geography: assignment.geography || '',
        economicDevelop: assignment.economicDevelop || '',
        society: assignment.society || '',
        technological: assignment.technological || '',
        video: assignment.video || '',
      });
      setSelectedArticles(
        assignment.articles ? assignment.articles.map((a) => a._id) : [],
      );
      setCurrentAssignmentId(assignment._id);
    } else {
      setEditData({
        startDate: '',
        endDate: '',
        articles: [],
        geography: '',
        economicDevelop: '',
        society: '',
        technological: '',
        video: '',
      });
      setSelectedArticles([]);
      setCurrentAssignmentId('');
    }
  };

  useEffect(() => {
    if (selectedPreset) {
      fetchAssignment();
    }
  }, [selectedPreset]);

  const del = async (assignmentId) => {
    setRemoveConfirm({ active: false, key: null });
    const response = await getReq({
      url: `/api/quiz/assignments/${assignmentId}`,
      method: 'delete',
    });
    if (response && response.status === 'success') {
      fetchAssignment();
    }
  };

  const CheckboxCell = ({ row }) => (
    <input
      type="checkbox"
      checked={selectedArticles.includes(row._id)}
      onChange={() => {
        if (selectedArticles.includes(row._id)) {
          setSelectedArticles(selectedArticles.filter((id) => id !== row._id));
        } else {
          setSelectedArticles([...selectedArticles, row._id]);
        }
      }}
    />
  );

  CheckboxCell.displayName = 'CheckboxCell';

  // Helper to get translated subject label
  const getSubjectLabel = (key) => {
    const lang = t('lang') || 'en';
    const subjectObj = subjects[key];
    if (!subjectObj) return key;
    // Try i18n translation first, fallback to config
    const i18nKey = `subjects.${key}`;
    const i18nLabel = t(i18nKey);
    if (i18nLabel !== i18nKey) return i18nLabel;
    return getLang(subjectObj, lang);
  };

  return (
    <div>
      <ModalConfirm
        show={removeConfirm.active}
        onReject={() => setRemoveConfirm({ active: false, key: null })}
        onConfirm={() => del(removeConfirm.key)}
        theme="danger"
      >
        {t('Are you sure you want to delete?')}
      </ModalConfirm>
      <Header>{t('Assignment Management')}</Header>

      <div className="alert alert-info mb-3">
        <strong>{t('How to use')}:</strong>{' '}
        {t('Select a preset assignment, edit dates and articles, then save.')}
      </div>

      {/* Preset Selection */}
      <div className="mb-3">
        <div className="form-group">
          <label>
            <strong>{t('Select Preset Assignment')}:</strong>
          </label>
          <select
            className="form-control form-control-lg"
            value={selectedPreset}
            onChange={(e) => {
              setSelectedPreset(e.target.value);
              setEditMode(false);
              setCurrentAssignmentId('');
            }}
          >
            <option value="">{t('Select a preset')}</option>
            {preSetAssignments.map((preset) => (
              <option key={preset.key} value={preset.key}>
                {preset.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedPreset && (
        <div className="card mb-3">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              {preSetAssignments.find((p) => p.key === selectedPreset).label}
            </h5>
            <button
              className="btn btn-primary"
              onClick={() => setEditMode(!editMode)}
            >
              {editMode ? t('Cancel') : t('Edit')}
            </button>
          </div>
          <div className="card-body">
            {editMode ? (
              <Formik
                initialValues={editData}
                validationSchema={schema}
                onSubmit={async (values, { setSubmitting }) => {
                  if (selectedArticles.length !== 20) {
                    setArticleCountError(
                      t('Please select exactly 20 articles'),
                    );
                    setSubmitting(false);
                    return;
                  }
                  setArticleCountError('');

                  const data = {
                    key: selectedPreset,
                    lv: preSetAssignments.find((p) => p.key === selectedPreset)
                      .lv,
                    startDate: moment(values.startDate)
                      .utcOffset(8 * 60)
                      .startOf('day')
                      .toDate(),
                    endDate: moment(values.endDate)
                      .utcOffset(8 * 60)
                      .endOf('day')
                      .toDate(),
                    articles: selectedArticles,
                    geography: Number(values.geography),
                    economicDevelop: Number(values.economicDevelop),
                    society: Number(values.society),
                    technological: Number(values.technological),
                    video: Number(values.video),
                  };

                  const response = await getReq({
                    url: currentAssignmentId
                      ? `/api/quiz/assignments/${currentAssignmentId}`
                      : '/api/quiz/assignments',
                    method: currentAssignmentId ? 'put' : 'post',
                    data,
                  });

                  if (response && response.status === 'success') {
                    setEditMode(false);
                    fetchAssignment();
                  }

                  setSubmitting(false);
                }}
              >
                {({
                  values, errors, touched, setFieldValue, isSubmitting,
                }) => (
                  <Form>
                    <FormHttpAlert res={res} />
                    <div className="form-row mb-3">
                      <div className="col-md-6">
                        <label>{t('Start Date')}</label>
                        <input
                          type="date"
                          className="form-control"
                          value={values.startDate}
                          onChange={(e) => setFieldValue('startDate', e.target.value)
                          }
                        />
                        {touched.startDate && errors.startDate && (
                          <div className="text-danger">{errors.startDate}</div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label>{t('End Date')}</label>
                        <input
                          type="date"
                          className="form-control"
                          value={values.endDate}
                          onChange={(e) => setFieldValue('endDate', e.target.value)
                          }
                        />
                        {touched.endDate && errors.endDate && (
                          <div className="text-danger">{errors.endDate}</div>
                        )}
                      </div>
                    </div>
                    <div className="form-row mb-3">
                      <div className="col-md-6">
                        <label>{getSubjectLabel('geography')}</label>
                        <input
                          type="number"
                          min="1"
                          className="form-control"
                          value={values.geography}
                          onChange={(e) => setFieldValue('geography', e.target.value)
                          }
                        />
                        {touched.geography && errors.geography && (
                          <div className="text-danger">{errors.geography}</div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label>{getSubjectLabel('economicDevelop')}</label>
                        <input
                          type="number"
                          min="1"
                          className="form-control"
                          value={values.economicDevelop}
                          onChange={(e) => setFieldValue('economicDevelop', e.target.value)
                          }
                        />
                        {touched.economicDevelop && errors.economicDevelop && (
                          <div className="text-danger">
                            {errors.economicDevelop}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="form-row mb-3">
                      <div className="col-md-6">
                        <label>{getSubjectLabel('society')}</label>
                        <input
                          type="number"
                          min="1"
                          className="form-control"
                          value={values.society}
                          onChange={(e) => setFieldValue('society', e.target.value)
                          }
                        />
                        {touched.society && errors.society && (
                          <div className="text-danger">{errors.society}</div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label>{getSubjectLabel('technological')}</label>
                        <input
                          type="number"
                          min="1"
                          className="form-control"
                          value={values.technological}
                          onChange={(e) => setFieldValue('technological', e.target.value)
                          }
                        />
                        {touched.technological && errors.technological && (
                          <div className="text-danger">
                            {errors.technological}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="form-row mb-3">
                      <div className="col-md-6">
                        <label>{t('video')}</label>
                        <input
                          type="number"
                          min="1"
                          className="form-control"
                          value={values.video}
                          onChange={(e) => setFieldValue('video', e.target.value)
                          }
                        />
                        {touched.video && errors.video && (
                          <div className="text-danger">{errors.video}</div>
                        )}
                      </div>
                    </div>
                    <div className="mb-3">
                      <h6>
                        {t('Selected Articles')}: {selectedArticles.length}
                      </h6>
                      {articleCountError && (
                        <div className="text-danger mb-2">
                          {articleCountError}
                        </div>
                      )}
                      {editData.articles.length > 0 && (
                        <div className="mb-2">
                          <strong>{t('Current Articles')}:</strong>
                          <ul>
                            {editData.articles.map((article) => (
                              <li key={article._id}>
                                {article.title}
                                <button
                                  className="btn btn-sm btn-outline-danger ml-2"
                                  onClick={() => {
                                    setEditData({
                                      ...editData,
                                      articles: editData.articles.filter(
                                        (a) => a._id !== article._id,
                                      ),
                                    });
                                    setSelectedArticles(
                                      selectedArticles.filter(
                                        (id) => id !== article._id,
                                      ),
                                    );
                                  }}
                                >
                                  {t('Remove')}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        type="submit"
                        className="btn btn-success"
                        disabled={isSubmitting}
                      >
                        {t('Save')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setEditMode(false);
                          fetchAssignment();
                        }}
                      >
                        {t('Cancel')}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            ) : (
              <div>
                <p>
                  <strong>{t('Start Date')}:</strong>{' '}
                  {editData.startDate || t('Not set')}
                </p>
                <p>
                  <strong>{t('End Date')}:</strong>{' '}
                  {editData.endDate || t('Not set')}
                </p>
                <p>
                  <strong>{getSubjectLabel('geography')}:</strong>{' '}
                  {editData.geography || 0}
                </p>
                <p>
                  <strong>{getSubjectLabel('economicDevelop')}:</strong>{' '}
                  {editData.economicDevelop || 0}
                </p>
                <p>
                  <strong>{getSubjectLabel('society')}:</strong>{' '}
                  {editData.society || 0}
                </p>
                <p>
                  <strong>{getSubjectLabel('technological')}:</strong>{' '}
                  {editData.technological || 0}
                </p>
                <p>
                  <strong>{t('video')}:</strong> {editData.video || 0}
                </p>
                <p>
                  <strong>{t('Articles')}:</strong> {editData.articles.length}
                </p>
                {editData.articles.length > 0 && (
                  <ul>
                    {editData.articles.map((article) => (
                      <li key={article._id}>{article.title}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {selectedPreset && editMode && (
        <div>
          <h5 className="mb-3">{t('Select Articles')}</h5>
          <ListHttpTable
            {...{
              api: { url: '/api/quiz/articles' },
              sort: { title: 1 },
              cols: [
                {
                  key: 'selected',
                  children: '',
                  width: '50px',
                  getter: function renderCheckboxCell(v, row) {
                    return <CheckboxCell row={row} />;
                  },
                },
                { key: 'refCode', children: t('Code'), sortable: true },
                { key: 'title', children: t('Title'), sortable: true },
                {
                  key: 'tags',
                  children: t('Tags'),
                  getter: (v) => (v || []).join(', '),
                },

                {
                  key: 'type',
                  children: t('type'),
                  getter: (v) => t(v),
                },
                {
                  key: 'subjects',
                  children: t('Subjects'),
                  getter: (v) => {
                    const subjectArray = v.map((subjectKey) => getSubjectLabel(subjectKey));
                    return subjectArray.join(', ') || '';
                  },
                },
              ],
              filter: { search },
            }}
          >
            <input
              type="text"
              className="form-control"
              placeholder={`${t('Search')}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </ListHttpTable>
        </div>
      )}
    </div>
  );
};

export default PageAssignments;
